import AbstractComponent from "./abstract-component";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const MOBILE_BREAKPOINT = 1000;

export default class Garanties extends AbstractComponent {
	constructor(el) {
		super(el);
		this.scrollTrigger = null;
		this.gapCompleted = false;
		this.flipCompleted = false;
		this.resizeTimer = null;

		this.initDomElements();
		this.setup = this.setup.bind(this);
		this.handleResize = this.handleResize.bind(this);

		this.setup();
		window.addEventListener("resize", this.handleResize);
		window.addEventListener("load", () => ScrollTrigger.refresh());
	}

	initDomElements() {
		this.container = this.$el.querySelector(".c-garanties__cards");
		this.header = this.$el.querySelector(".c-garanties__sticky-header");
		this.cards = [...this.$el.querySelectorAll(".c-garanties__card")];
		this.card1 = this.cards[0];
		this.card2 = this.cards[1];
		this.card3 = this.cards[2];
		this.sideCards = [this.card1, this.card3].filter(Boolean);
	}

	cleanup() {
		this.scrollTrigger?.kill();
		this.scrollTrigger = null;
	}

	setup() {
		this.cleanup();

		if (!this.container || !this.cards.length) return;

		this.cards.forEach((card) => gsap.set(card, { clearProps: "all" }));
		gsap.set(this.container, { clearProps: "all" });
		if (this.header) {
			gsap.set(this.header, { clearProps: "all" });
		}

		this.gapCompleted = false;
		this.flipCompleted = false;

		if (window.innerWidth < MOBILE_BREAKPOINT) return;

		if (this.header) {
			gsap.set(this.header, { y: 40, opacity: 0 });
		}

		this.scrollTrigger = ScrollTrigger.create({
			trigger: this.$el,
			start: "top top",
			end: `+=${window.innerHeight * 4}`,
			scrub: 1,
			pin: true,
			pinSpacing: true,
			anticipatePin: 1,
			onUpdate: (self) => this.onUpdate(self.progress),
		});
	}

	onUpdate(progress) {
		if (this.header) {
			if (progress >= 0.1 && progress <= 0.25) {
				const headerProgress = gsap.utils.mapRange(0.1, 0.25, 0, 1, progress);
				gsap.set(this.header, {
					y: gsap.utils.mapRange(0, 1, 40, 0, headerProgress),
					opacity: headerProgress,
				});
			} else if (progress < 0.1) {
				gsap.set(this.header, { y: 40, opacity: 0 });
			} else {
				gsap.set(this.header, { y: 0, opacity: 1 });
			}
		}

		if (progress <= 0.25) {
			const width = gsap.utils.mapRange(0, 0.25, 80, 60, progress);
			gsap.set(this.container, { width: `${width}%` });
		} else {
			gsap.set(this.container, { width: "60%" });
		}

		if (progress >= 0.35 && !this.gapCompleted) {
			gsap.to(this.container, { gap: "16px", duration: 0.5, ease: "power3.out" });
			gsap.to(this.cards, { borderRadius: "12px", duration: 0.5, ease: "power3.out" });
			this.gapCompleted = true;
		} else if (progress < 0.35 && this.gapCompleted) {
			gsap.to(this.container, { gap: "0px", duration: 0.5, ease: "power3.out" });
			if (this.card1) {
				gsap.to(this.card1, { borderRadius: "12px 0 0 12px", duration: 0.5, ease: "power3.out" });
			}
			if (this.card2) {
				gsap.to(this.card2, { borderRadius: "0px", duration: 0.5, ease: "power3.out" });
			}
			if (this.card3) {
				gsap.to(this.card3, { borderRadius: "0 12px 12px 0", duration: 0.5, ease: "power3.out" });
			}
			this.gapCompleted = false;
		}

		if (progress >= 0.7 && !this.flipCompleted) {
			gsap.to(this.cards, {
				rotationY: 180,
				duration: 0.75,
				ease: "power3.inOut",
				stagger: 0.1,
			});
			gsap.to(this.sideCards, {
				y: 30,
				xPercent: (i) => [-30, 30][i],
				rotationZ: (i) => [-12, 12][i],
				duration: 0.75,
				ease: "power3.inOut",
			});
			this.flipCompleted = true;
		} else if (progress < 0.7 && this.flipCompleted) {
			gsap.to(this.cards, {
				rotationY: 0,
				duration: 0.75,
				ease: "power3.inOut",
				stagger: -0.1,
			});
			gsap.to(this.sideCards, {
				y: 0,
				xPercent: 0,
				rotationZ: 0,
				duration: 0.75,
				ease: "power3.inOut",
			});
			this.flipCompleted = false;
		}
	}

	handleResize() {
		clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(this.setup, 250);
	}

	destroy() {
		this.cleanup();
		clearTimeout(this.resizeTimer);
		window.removeEventListener("resize", this.handleResize);
	}
}
